/**
 * Render a server-generated wiki search snippet — the BE's `ts_headline`
 * output that wraps matched tokens in `<mark>...</mark>` brackets.
 *
 * The snippet is server-controlled (the literal `<mark>...</mark>`
 * template lives in `3mote-be-new/src/wiki/search.ts → TS_HEADLINE_OPTIONS`)
 * and no user input reaches the field, but we still parse it through a
 * fixed shape rather than reaching for `dangerouslySetInnerHTML`. Same
 * defense-in-depth posture as the chat mention renderer.
 *
 * The parser splits on the `<mark>...</mark>` regex and emits a mix of
 * plain-text spans + themed `<mark>` elements. The themed `<mark>` uses
 * `bg-accent/20 text-fg` so it adapts to the user's active theme (the
 * default browser `<mark>` background is hardcoded yellow + can clash
 * with the Obsidian / Midnight Blue dark themes).
 */

import type { ReactNode } from 'react'

/**
 * Bounded matcher — splits on `<mark>x</mark>` blocks. The character
 * class `[^<]*` inside the brackets prevents the regex from spanning
 * an accidental nested `<` (which `ts_headline` won't emit but the
 * defensive shape costs nothing). Captures the inner text so we can
 * keep it isolated from any prior / trailing literal segment.
 */
const MARK_REGEX = /<mark>([^<]*)<\/mark>/g

/**
 * Parse a server-rendered snippet into React nodes. Returns an array
 * suitable for direct rendering inside a JSX subtree — no key prop is
 * needed at the caller since the renderer attaches stable keys to
 * every node.
 *
 * Title-only matches pass `snippet: null` and the caller renders
 * nothing — this renderer expects a non-null string. The empty-string
 * case returns an empty array (idiomatic React: zero children renders
 * to no DOM).
 */
export function renderSearchSnippet(snippet: string): ReactNode[] {
  const nodes: ReactNode[] = []
  let cursor = 0
  let key = 0
  // Reset the regex's lastIndex between calls — module-level `g`-flag
  // regex objects retain state across invocations.
  MARK_REGEX.lastIndex = 0
  let match: RegExpExecArray | null = MARK_REGEX.exec(snippet)
  while (match !== null) {
    // Literal text before this match.
    if (match.index > cursor) {
      nodes.push(<span key={`s-${key}`}>{snippet.slice(cursor, match.index)}</span>)
      key += 1
    }
    // The highlighted token. The inner text is captured from group 1
    // and rendered as plain children — React escapes it automatically,
    // so a hostile inner like `<script>` would still render verbatim
    // as text, not as a DOM element.
    nodes.push(
      <mark
        key={`m-${key}`}
        className="rounded-sm bg-amber-200/70 px-0.5 text-fg dark:bg-amber-300/30"
      >
        {match[1] ?? ''}
      </mark>,
    )
    key += 1
    cursor = match.index + match[0].length
    match = MARK_REGEX.exec(snippet)
  }
  // Trailing literal segment after the last match.
  if (cursor < snippet.length) {
    nodes.push(<span key={`s-${key}`}>{snippet.slice(cursor)}</span>)
  }
  return nodes
}
